import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { OAuthGrant } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * The signed-in user's OAuth grants — the hosts they approved on the
 * consent page (ADR-058). Per user, not per workspace, so every role
 * sees and manages only their own.
 *
 * While the project's OAuth server is off, GoTrue answers 404
 * `feature_disabled`; nobody can hold a grant then, so that reads as none.
 */
export function useOAuthGrants() {
  return useQuery<OAuthGrant[], Error>({
    queryKey: ["oauth-grants"],
    queryFn: async () => {
      const { data, error } = await supabase.auth.oauth.listGrants();
      if (error?.code === "feature_disabled") return [];
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Revoking stops refresh and ends the client's sessions. tenant-service
 * checks an OAuth-issued token's session on `/v2/mcp` with a 30-second
 * cache, so a token the client already holds stops working within 30
 * seconds (ADR-058, KOP-102).
 */
export function useRevokeOAuthGrant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientId: string) => {
      const { error } = await supabase.auth.oauth.revokeGrant({ clientId });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Access revoked");
    },
    // Also on failure: a 404 usually means the grant was revoked elsewhere
    // (another tab), and the list should stop showing it.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["oauth-grants"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to revoke access");
    },
  });
}
